import { CalendarClock, ClipboardList, Plus } from "lucide-react";
import { dayMinuteToLabel } from "../../../domain/rules/schedule";
import type { FlightInstance, SaveGame, ScheduleSource, WorldData } from "../../../domain/types";
import type { CustomFlightDraft } from "../../gameSession";
import { formatCustomFlightKind, formatCustomFlightStatus } from "../../format";
import { AirportSearch } from "../../components/AirportSearch";
import { PanelHeader } from "../../components/Panel";
import { SearchableChoice } from "./PlayerShared";
import { formatFlightInstanceStatus } from "./PlayerTimeline";
import { safeDistance } from "./playerData";


export function CustomFlightView(props: {
  save: SaveGame;
  world: WorldData;
  schedule: ScheduleSource;
  customFlightDraft: CustomFlightDraft;
  setCustomFlightDraft: (value: CustomFlightDraft) => void;
  onCreateCustomFlight: () => void;
}) {
  return (
    <section className="player-view recovery-layout">
      <section className="panel recovery-create-panel">
        <PanelHeader icon={<CalendarClock size={15} />} title="自定义航班" code="ADHOC" />
        <CustomFlightForm
          save={props.save}
          world={props.world}
          schedule={props.schedule}
          draft={props.customFlightDraft}
          setDraft={props.setCustomFlightDraft}
          onSubmit={props.onCreateCustomFlight}
        />
      </section>
      <section className="panel recovery-open-panel">
        <PanelHeader icon={<ClipboardList size={15} />} title="自定义航班队列" code="OPEN" />
        <CustomFlightQueue save={props.save} />
      </section>
    </section>
  );
}

function CustomFlightForm(props: {
  save: SaveGame;
  world: WorldData;
  schedule: ScheduleSource;
  draft: CustomFlightDraft;
  setDraft: (value: CustomFlightDraft) => void;
  onSubmit: () => void;
}) {
  const distance = safeDistance(props.world, props.draft.origin, props.draft.destination);
  const sourceFlights = props.save.activeOpsDay?.flights
    .filter((flight) => flight.sourceType === "rotation")
    .sort((a, b) => a.estimatedDepartureMinute - b.estimatedDepartureMinute || a.flightNo.localeCompare(b.flightNo)) ?? [];
  const selectedSource = sourceFlights.find((flight) => flight.id === props.draft.recoveryForFlightInstanceId);
  const sourceOptions = [
    { value: "", label: "独立恢复 / 不承接旅客", detail: "不绑定原航班实例" },
    ...sourceFlights.map((flight) => ({
      value: flight.id,
      label: `${flight.flightNo} / ${flight.origin}-${flight.destination} / ${formatFlightInstanceStatus(flight.status)}`,
      detail: `原计划 ${flight.plannedOrigin}-${flight.plannedDestination} / ${dayMinuteToLabel(flight.estimatedArrivalMinute)}`
    }))
  ];
  return (
    <div className="planner-grid recovery-form-grid">
      <SearchableChoice
        id="recovery-kind"
        label="类型"
        value={props.draft.kind}
        options={["recovery", "positioning"].map((kind) => ({
          value: kind,
          label: formatCustomFlightKind(kind as CustomFlightDraft["kind"])
        }))}
        onChange={(kind) =>
          props.setDraft({
            ...props.draft,
            kind: kind as CustomFlightDraft["kind"],
            recoveryForFlightInstanceId: recoverySourceForKind(kind as CustomFlightDraft["kind"], props.draft.recoveryForFlightInstanceId)
          })
        }
      />
      <SearchableChoice
        id="recovery-source-flight"
        label="承接异常航班"
        value={visibleRecoverySourceValue(props.draft)}
        options={sourceOptions}
        onChange={(recoveryForFlightInstanceId) => props.setDraft({ ...props.draft, recoveryForFlightInstanceId })}
        placeholder="航班号 / 航线 / 状态"
      />
      <AirportSearch id="recovery-origin" label="始发机场" world={props.world} value={props.draft.origin} onChange={(origin) => props.setDraft({ ...props.draft, origin })} />
      <AirportSearch id="recovery-destination" label="到达机场" world={props.world} value={props.draft.destination} onChange={(destination) => props.setDraft({ ...props.draft, destination })} />
      <SearchableChoice
        id="recovery-aircraft-type"
        label="计划机型"
        value={props.draft.aircraftTypeId}
        options={props.world.aircraftTypes.map((type) => ({
          value: type.id,
          label: `${type.name}（${type.id.toUpperCase()}）`,
          detail: `${type.seatCapacity} 座 / ${type.rangeKm.toLocaleString("en-US")} km`
        }))}
        onChange={(aircraftTypeId) => props.setDraft({ ...props.draft, aircraftTypeId })}
        placeholder="机型 / 代码"
      />
      <label>
        计划日偏移
        <input
          type="number"
          min={0}
          max={6}
          value={props.draft.scheduledOffsetDays}
          onChange={(event) => props.setDraft({ ...props.draft, scheduledOffsetDays: Number(event.target.value) })}
        />
      </label>
      <label>
        起飞时刻
        <input type="time" value={props.draft.departureTimeLocal} onChange={(event) => props.setDraft({ ...props.draft, departureTimeLocal: event.target.value })} />
      </label>
      <div className="form-readout">
        距离 {distanceReadout(distance)} / {selectedSourceReadout(selectedSource)}
      </div>
      <button className="primary-command" onClick={props.onSubmit}>
        <Plus size={15} />
        创建{formatCustomFlightKind(props.draft.kind)}
      </button>
    </div>
  );
}

function CustomFlightQueue({ save }: { save: SaveGame }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>状态</th>
            <th>任务</th>
            <th>航段</th>
            <th>计划</th>
            <th>机型</th>
            <th>承接</th>
            <th>旅客</th>
          </tr>
        </thead>
        <tbody>
          {save.recoveryFlights.map((flight) => (
            <tr key={flight.id}>
              <td>{formatCustomFlightStatus(flight.status)}</td>
              <td>{flight.title}</td>
              <td>{flight.origin}-{flight.destination}</td>
              <td>
                D{flight.scheduledDay} {flight.departureTimeLocal}
              </td>
              <td>{flight.aircraftTypeId.toUpperCase()}</td>
              <td>{recoverySourceLabel(save, flight.recoveryForFlightInstanceId)}</td>
              <td>{flight.passengerTransfers?.reduce((sum, transfer) => sum + transfer.passengers, 0) ?? 0} pax</td>
            </tr>
          ))}
          {save.recoveryFlights.length === 0 && (
            <tr>
              <td colSpan={7}>暂无自定义航班。</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function recoverySourceLabel(save: SaveGame, flightInstanceId: string | undefined): string {
  if (!flightInstanceId) {
    return "独立";
  }
  const source = save.activeOpsDay?.flights.find((flight) => flight.id === flightInstanceId);
  return recoverySourceFlightLabel(source, flightInstanceId);
}

function recoverySourceForKind(kind: CustomFlightDraft["kind"], currentSourceId: string): string {
  if (kind === "positioning") {
    return "";
  }
  return currentSourceId;
}

function visibleRecoverySourceValue(draft: CustomFlightDraft): string {
  if (draft.kind === "positioning") {
    return "";
  }
  return draft.recoveryForFlightInstanceId;
}

function distanceReadout(distance: number | null): string {
  if (distance === null) {
    return "等待机场代码";
  }
  return `${distance.toLocaleString("en-US")} km`;
}

function selectedSourceReadout(selectedSource: FlightInstance | undefined): string {
  if (selectedSource) {
    return `承接 ${selectedSource.flightNo} 旅客，创建后自动估算转运量。`;
  }
  return "独立任务，不跟随航班串。";
}

function recoverySourceFlightLabel(source: FlightInstance | undefined, fallbackId: string): string {
  if (source) {
    return `${source.flightNo} / ${source.origin}-${source.destination}`;
  }
  return fallbackId;
}
